import React from 'react';
import { EappEnumItem, EappEnumItemInterface } from './eAppEnumItem';

export interface EappEnumInterface {
  values:EappEnumItemInterface[];
}
export class EappEnum implements EappEnumInterface {
  // #region -- Static methods --

  static toSet(values:EappEnumItemInterface[]):Set<EappEnumItemInterface> { return new Set(values); }

  // #endregion

  // #region -- Properties --

  values:EappEnumItemInterface[];

  // #endregion
  // #region -- Constructors methods --

  constructor(values:EappEnumItemInterface[] = []) {
    this.values = values;
  }

  // #endregion
  // #region -- Custom Methods --

  /**
   * @returns {[k: string]: string;} key value pairs ready to be spread in locale object structures
   */
  asLocaleEn() {
    const data: Map<string, string> = new Map<string, string>();

    this.values?.forEach((x) => {
      if (x?.i18nField !== undefined && x?.i18nField !== null) { // permit falsey values as the key
        data.set(x.i18nField, x.text);
      }
    });
    return Object.fromEntries(data);
  }

  asMap():Map<string, EappEnumItemInterface> {
    const data = new Map<string, EappEnumItemInterface>();
    this.values.forEach((x) => data.set(x.key, x));
    return data;
  }

  asOptionsValues() {
    const data = this.values.map((x, index) => (
      <option key={`${x.key}-${index}`} value={`${x.value ?? ''}`}>
        {`${x.text ?? ''}`}
      </option>
    ));
    return data;
  }

  getKeys(): string[] {
    return this.values.map((x) => x.key) || [];
  }

  /**
   * For validation, use getValues() in stead
   * @returns { string[] } A string array of only the text attributes
   */
  getText(): string[] {
    return this.values.map((x) => x.text) || [];
  }

  /**
   * Best used for validation
   * @returns A string array of only the value attributes
   */
  getValues(): string[] {
    return this.values.map((x) => x.value) || [];
  }

  /**
   * @returns { boolean } true if values return truthy
   */
  has() {
    return !!this.values;
  }

  length() {
    return this.values?.length ?? 0;
  }

  // #endregion
  // #region -- Pass-Through "Compatibility" methods -- */

  /**
   * Provided for some backwards compatibility prefer direct operations on values.
   * @param callback A function that returns true to keep the item, or false to exclude it
   * @param thisArg Value to use as 'this' when executing predicate
   * @returns New array with only the items for which predicate returned true
   */
  filter(
    callback: (value: EappEnumItemInterface, index?: number, array?: EappEnumItemInterface[]) => boolean,
    thisArg?: any
  ): EappEnumItemInterface[] {
    if (this.has()) {
      return this.values.filter(callback, thisArg);
    }
    return [];
  }

  /**
   * Provided for some backwards compatibility prefer direct operations on values.
   * @param callback A function that returns true for the desired item
   * @param thisArg Value to use as 'this' when executing predicate
   * @returns First matching item or undefined if no match is found
   */
  find(
    callback: (value: EappEnumItemInterface, index?: number, array?: EappEnumItemInterface[]) => boolean,
    thisArg?: any
  ): EappEnumItemInterface | undefined {
    if (this.has()) {
      return this.values.find(callback, thisArg);
    }
    return undefined;
  }

  /**
   * Provided for some backwards compatibility prefer direct operations on values.
   * @param callback Function to execute for each element
   * @param thisArg Value to use as 'this' when executing callback
   */
  forEach(
    callback: (value: EappEnumItemInterface, index?: number, array?: EappEnumItemInterface[]) => void,
    thisArg?: any
  ): void {
    if (this.has()) {
      this.values.forEach(callback, thisArg);
    }
  }

  /**
   * Provided for some backwards compatibility prefer direct operations on values.
   * @param searchElement The element to search for
   * @returns true if item is found
   */
  includes(searchElement: EappEnumItemInterface, fromIndex?: number): boolean {
    if (this.has()) {
      return this.values.includes(searchElement, fromIndex);
    }
    return false;
  }

  /**
   * Provided for some backwards compatibility prefer direct operations on values.
   * @template U The type returned by the callback function
   * @param callback A function that produces an element of the new array
   * @param thisArg Value to use as 'this' when executing callback
   * @returns new array with each element transformed by the callback
   */
  map<U>(
    callback: (value: EappEnumItemInterface, index?: number, array?: EappEnumItemInterface[]) => U,
    thisArg?: any
  ): U[] {
    if (this.has()) {
      return this.values.map(callback, thisArg);
    }
    return [];
  }
  // #endregion
}
